import React, { useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { KeywordItem } from '../../types/seo';
import { parseGscRowsToKeywords } from '../../utils/gscSyncEngine';
import { parseSearchConsoleCsv } from '../../utils/gscCsvImport';

interface Props {
  open: boolean;
  onClose: () => void;
  onMergeKeywords: (keywords: KeywordItem[]) => void;
}

export default function GoogleSearchConsoleSyncModal({ open, onClose, onMergeKeywords }: Props) {
  const [filename, setFilename] = useState('');
  const [csv, setCsv] = useState('');
  const [minImpressions, setMinImpressions] = useState(0);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFilename(file.name);
    setCsv(await file.text());
    setMessage(null);
  };

  const handleImport = () => {
    try {
      const rows = parseSearchConsoleCsv(csv).filter((row) => row.impressions >= minImpressions);
      if (!rows.length) throw new Error('No queries meet the impressions filter.');
      onMergeKeywords(parseGscRowsToKeywords(rows));
      setMessage({ text: `Imported ${rows.length} measured queries from ${filename}. The figures reflect the date range selected in Search Console when you exported the file.`, error: false });
    } catch (error) {
      setMessage({ text: error instanceof Error ? error.message : 'Unable to read this CSV.', error: true });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import Google Search Console queries</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">In Search Console, open Performance → Search results → Queries and export a CSV. This CRM currently imports that file; it does not have a live Google connection. Never upload a service account key into the browser.</Alert>
          <Box>
            <Button component="label" variant="outlined">
              Choose Queries CSV
              <input type="file" accept=".csv,text/csv" hidden onChange={handleFile} />
            </Button>
            {filename && <Typography variant="body2" sx={{ mt: 1 }}>{filename}</Typography>}
          </Box>
          <TextField label="Minimum impressions" type="number" value={minImpressions} onChange={(event) => setMinImpressions(Math.max(0, Number(event.target.value) || 0))} />
          {message && <Alert severity={message.error ? 'error' : 'success'}>{message.text}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handleImport} disabled={!csv}>Import measured queries</Button>
      </DialogActions>
    </Dialog>
  );
}
